//Modules
import { AntdModule } from './antd.module';
import { CommonModule, NgOptimizedImage } from '@angular/common';
import { ComponentLibraryModule } from 'projects/component-library/component-library.module';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { FroalaEditorModule, FroalaViewModule } from 'angular-froala-wysiwyg';
import { ModuleWithProviders, NgModule } from '@angular/core';
import { RouterModule } from '@angular/router';
import { TranslateModule } from '@ngx-translate/core';

//Components
import { AddressDetailsFormComponent } from './components/forms/address-details-form.component';
import { AlertComponent } from './components/alert/alert.component';
import { BaseHeaderComponent } from './components/header/base-header.component';
import { BlankLayoutComponent } from './components/layouts/blank-layout/blank-layout.component';
import { BulkUserActionsButtonComponent } from './users/list/bulk-actions-button.component';
import { ChangePasswordModalComponent } from './components/settings/change-password-modal/change-password-modal.component';
import { CommunicationContentComponent } from './components/communications/communication-content/communication-content.component';
import { CommunicationFormComponent } from './components/communications/communication-content/communication-form/communication-form.component';
import { CommunicationsComponent } from './components/communications/communications.component';
import { CompanyDetailsFormComponent } from './components/forms/company-details-form.component';
import { CompanyWebDetailsFormComponent } from './components/forms/web-details-form.component';
import { CountriesService } from './services/countries/countries.service';
import { CountryInputSelectComponent } from './components/forms/country-select-input/country-select-input.component';
import { CultureSelectInputComponent } from './components/forms/culture-select-input/culture-select-input.component';
import { CustomerCompanyStatusComponent } from './components/table-items/customer-company-status/customer-company-status.component';
import { CustomerLogoComponent } from './components/customer-logo/customer-logo.component';
import { DeleteUserConfirmationModalComponent } from './users/delete/delete-user-confirmation-modal.component';
import { DeleteUsersBulkRowComponent, DeleteUsersBulkModalComponent } from './users/delete/delete-users-bulk-modal.component';
import { DividerComponent } from './components/divider/divider.component';
import { EditUserButtonComponent } from './users/edit/edit-user-button.component';
import { EditUserModalComponent } from './users/edit/edit-user-modal.component';
import { EditUsersBulkModalComponent } from './users/edit/edit-users-bulk-modal.component';
import { ErrorTipComponent } from './components/error-tip/error-tip.component';
import { ForceTanComponent } from './components/table-items/force-tan/force-tan.component';
import { FormItemDateComponent } from './components/form-items/form-item-date/form-item-date.component';
import { ImageUploadComponent } from './components/forms/image-upload/image-upload.component';
import { InputComponent } from './components/forms/input/input.component';
import { InspectionCompanyHeaderComponent } from './components/inspection-companies/inspection-company-header/inspection-company-header.component';
import { InspectionCompanyLogoUploaderComponent } from './components/inspection-companies/logo-uploader/logo-uploader.component';
import { InviteUsersBulkFormComponent } from './users/invite/invite-bulk-users.component';
import { InviteUsersButtonComponent } from './users/list/invite-users-button.component';
import { InviteUsersModalComponent } from './users/invite/invite-users-modal.component';
import { LanguageButtonComponent } from './components/language-button/language-button.component';
import { ListUsersComponent } from './users/list/list-users.component';
import { ListUsersFilterComponent } from './users/list/list-users-filter.component';
import { ListUsersTableComponent } from './users/list/list-users-table.component';
import { ListUsersToolbarComponent } from './users/list/list-users-toolbar.component';
import { LogoComponent } from './components/logo/logo.component';
import { NotFoundLayoutComponent } from './components/layouts/not-found-layout/not-found-layout.component';
import { PageHeaderActionsComponent } from './components/page-header/page-header-actions/page-header-actions.component';
import { PageHeaderComponent } from './components/page-header/page-header.component';
import { RolesInfoPageComponent } from './roles/pages/info/roles-info.page';
import { RolesPermissionsMatrixColumnComponent } from './roles/components/roles-permissions-matrix/column/roles-permissions-matrix-column.component';
import { RolesPermissionsMatrixComponent } from './roles/components/roles-permissions-matrix/roles-permissions-matrix.component';
import { RolesSelectInputComponent } from './components/forms/roles-select-input/roles-select-input.component';
import { SelectComponent } from './components/forms/select/select.component';
import { SettingNavBarComponent } from './components/setting-nav-bar/setting-nav-bar.component';
import { SidebarActionsComponent } from './components/sidebar/sidebar-actions.component';
import { SidebarComponent } from './components/sidebar/sidebar.component';
import { UserProfileButtonComponent } from './components/settings/user-profile-button/user-profile-button.component';
import { UserSettingsFormComponent } from './components/settings/user-settings-form/user-settings-form.component';
import { UserStatusBadgeComponent } from './users/list/user-status-badge.component';

//Services
import { AccountService } from './services/account/account.service';
import { InspectionCompanyCustomerContractService } from './services/inspection-companies/inspection-company.customer-contract.service';
import { InspectionCompanyUserService } from './services/inspection-companies/inspection-company.user.service';
import { LanguageService } from './services/language.service';
import { RoleService } from './services/roles/role.service';
import { SecurityService } from './services/security.service';
import { UserService } from './services/users/user.service';

//Guards
import { LoginGuard } from './guards/login.guard';

//Pipes
import { FormControlPipe } from './pipes/form-control.pipe';
import { LocalizedDate } from './pipes/localized-date.pipe';

//Directives
import { IfPermissionDirective } from '../shared/directives/if-permission.directive';

const layoutComponents = [BlankLayoutComponent, NotFoundLayoutComponent];
const components = [
  AddressDetailsFormComponent,
  AlertComponent,
  BaseHeaderComponent,
  BulkUserActionsButtonComponent,
  ChangePasswordModalComponent,
  CommunicationContentComponent,
  CommunicationFormComponent,
  CommunicationsComponent,
  CompanyDetailsFormComponent,
  CompanyWebDetailsFormComponent,
  CountryInputSelectComponent,
  CultureSelectInputComponent,
  CustomerCompanyStatusComponent,
  CustomerLogoComponent,
  DeleteUsersBulkModalComponent,
  DeleteUsersBulkRowComponent,
  DeleteUserConfirmationModalComponent,
  DividerComponent,
  EditUserButtonComponent,
  EditUserModalComponent,
  EditUsersBulkModalComponent,
  ErrorTipComponent,
  ForceTanComponent,
  FormItemDateComponent,
  ImageUploadComponent,
  InviteUsersModalComponent,
  InviteUsersButtonComponent,
  InviteUsersBulkFormComponent,
  InputComponent,
  InspectionCompanyHeaderComponent,
  InspectionCompanyLogoUploaderComponent,
  LanguageButtonComponent,
  ListUsersFilterComponent,
  ListUsersToolbarComponent,
  ListUsersTableComponent,
  ListUsersComponent,
  LogoComponent,
  PageHeaderActionsComponent,
  PageHeaderComponent,
  RolesInfoPageComponent,
  RolesPermissionsMatrixColumnComponent,
  RolesPermissionsMatrixComponent,
  RolesSelectInputComponent,
  SelectComponent,
  SettingNavBarComponent,
  SidebarActionsComponent,
  SidebarComponent,
  UserProfileButtonComponent,
  UserSettingsFormComponent,
  UserStatusBadgeComponent,
];
const pipes = [FormControlPipe, LocalizedDate];
const directives = [IfPermissionDirective];
const services = [
  AccountService,
  CountriesService,
  InspectionCompanyCustomerContractService,
  InspectionCompanyUserService,
  LanguageService,
  RoleService,
  SecurityService,
  UserService,
];
const guards = [LoginGuard];

@NgModule({
  declarations: [...layoutComponents, ...components, ...pipes, ...directives],
  imports: [
    AntdModule,
    CommonModule,
    RouterModule,
    TranslateModule,
    ReactiveFormsModule,
    FormsModule,
    ComponentLibraryModule,
    FroalaEditorModule.forRoot(),
    FroalaViewModule.forRoot(),
    NgOptimizedImage,
  ],
  exports: [
    AntdModule,
    TranslateModule,
    ReactiveFormsModule,
    ComponentLibraryModule,
    FroalaEditorModule,
    FroalaViewModule,
    ...layoutComponents,
    ...components,
    ...pipes,
    ...directives
  ]
})
export class SharedModule {
  static forRoot(): ModuleWithProviders<SharedModule> {
    return {
      ngModule: SharedModule,
      providers: [...services, ...guards]
    };
  }
}
